<template>
  <div class="member-user-filter">
    <div class="title-row">
      <h3 class="title" v-t="'appMemberUserFilter.search'" />
      <a
        class="clear"
        v-t="'appMemberUserFilter.action.clear'"
        v-on:click="$_clear"
      />
    </div>
    <input-keyword-user
      v-model="localKeyword"
      v-bind:idPrefix="'member-user'"
      v-bind:placeholder="$t('appMemberUserFilter.placeholder.keyword')"
      v-bind:keyword-targets="localCondition.search.keywordTargets"
      v-on:keyword-changed="$_onKeywordChanged"
      v-on:keyword-target-changed="$_onKeywordTargetChanged"
    />
    <member-user-filter-panel
      v-bind:condition="condition"
      v-on:condition-changed="$_onConditionChanged"
    />
  </div>
</template>

<script>
import InputKeywordUser from '@/components/AppInputKeywordUser.vue'
import MemberUserFilterPanel from '@/components/member/AppMemberUserFilterPanel.vue'
import { useUserStatusFilterCondition } from '@/composables/member/UseUserStatusFilterCondition'

export default {
  name: 'AppMemberUserFilter',
  components: {
    InputKeywordUser,
    MemberUserFilterPanel,
  },
  props: {
    /**
     * 絞り込み条件
     */
    condition: {
      type: Object,
      default: () => {},
    },
  },
  emits: [
    /**
     * 条件変更イベント
     * @param {Object} condition 条件
     */
    'condition-changed',
  ],
  setup() {
    const { getBlankCondition } = useUserStatusFilterCondition()
    return { getBlankCondition }
  },
  data: function () {
    return {
      // ローカル保持用絞り込み条件
      localCondition: this.condition,
      // 内部用キーワード(リアクティブ化を避けるため分離）
      localKeyword: this.condition?.search?.keyword || null,
    }
  },
  computed: {
    /**
     * パネル用condition
     * condition から search だけ分離したもの
     * @type {Object}
     */
    panelCondition: function () {
      const result = {}
      for (const [key, value] of Object.entries(this.localCondition)) {
        if (key !== 'search') {
          result[key] = value
        }
      }
      return result
    },
    /**
     * 変更検知用localCondition
     * @type {String}
     */
    localConditionForWatch: function () {
      return JSON.stringify(this.localCondition)
    },
    /**
     * props.condition と localCondition が値レベルで変わっている場合はtrue
     * @type {Boolean}
     */
    isConditionValueDifferent: function () {
      return (
        JSON.stringify(this.condition) !== JSON.stringify(this.localCondition)
      )
    },
  },
  watch: {
    condition: {
      deep: true,
      handler: function (value) {
        this.localCondition = value
        this.localKeyword = value?.search?.keyword || null
      },
    },
    localCondition: function (value) {
      if (this.isConditionValueDifferent) {
        this.$emit('condition-changed', value)
      }
    },
  },
  methods: {
    /**
     * 条件クリア
     */
    $_clear: function () {
      this.localCondition = this.getBlankCondition()
      this.localKeyword = null
      // store にも保存
      this.$store.dispatch(
        'userKeywordTarget/setKeywordTargets',
        this.localCondition.search.keywordTargets
      )
    },

    /**
     * キーワード変更時処理
     * @param {String} keyword キーワード
     */
    $_onKeywordChanged: function (keyword) {
      this.localCondition = {
        ...this.localCondition,
        search: {
          ...this.localCondition.search,
          keyword,
        },
      }
    },

    /**
     * キーワード検索対象変更時処理
     * @param {Array.<String>} keywordTargets キーワード検索対象
     */
    $_onKeywordTargetChanged: function (keywordTargets) {
      this.localCondition = {
        ...this.localCondition,
        search: {
          keyword: this.localKeyword,
          keywordTargets,
        },
      }
      // store にも保存
      this.$store.dispatch(
        'userKeywordTarget/setKeywordTargets',
        keywordTargets
      )
    },

    /**
     * パネルの絞り込み条件変更時処理
     * パネルからは頻回にイベントが発生するので間引く
     * @param {Object} condition 変更した絞り込み条件
     */
    $_onConditionChanged: function (condition) {
      this.localCondition = {
        ...this.localCondition,
        ...condition,
        search: {
          ...this.localCondition.search,
          keyword: this.localKeyword,
        },
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.member-user-filter {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  row-gap: 8px;
}

.title-row {
  width: 100%;
  padding: 0 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  line-height: 1;

  .title {
    font-size: $font-size-middle;
    font-weight: $font-weight-bold;
    margin: 0;
  }

  .clear {
    font-size: $font-size-middle;
    color: $text-color-link;
    text-decoration: $text-color-link underline;

    &:hover {
      cursor: pointer;
    }
  }
}
</style>
